import { generateQueryString } from "utils/query";
import { handleAPIError } from "handlers/setters";
import { downloadFile } from "utils";
import { getLocaleString } from "utils/localization/locale";
import Toaster from "components/Toaster";
import {
  instanceWithRetry,
  instanceMultipart,
} from "../../actions/axiosInstance";

export const getBankFileList = (params) =>
  instanceWithRetry
    .get(`/cash_app/bankfiles/${generateQueryString(params)}`)
    .catch((error) => {
      handleAPIError(error);
      Toaster(
        getLocaleString(
          "bank_files.error_fetching_files",
          "Error fetching bank files"
        ),
        "danger"
      );
      throw error;
    });

export const downloadBankFile = (presignedUrl, fileName) => {
  if (presignedUrl) {
    downloadFile(presignedUrl, fileName);
  } else {
    Toaster(
      getLocaleString(
        "bank_files.error_downloading_file",
        "Error downloading file - URL not available"
      ),
      "danger"
    );
  }
};

export const getBankFileDetails = (fileId) =>
  instanceWithRetry
    .get(`/cash_app/bankfiles/${fileId}/details`)
    .catch((error) => {
      handleAPIError(error);
      Toaster(
        getLocaleString(
          "bank_files.error_fetching_file_details",
          "Error fetching bank file details"
        ),
        "danger"
      );
      throw error;
    });

export const getBankFileLinkages = (fileId, summaryOnly = false) => {
  const params = summaryOnly ? "?summary_only=true" : "?summary_only=false";
  return instanceWithRetry
    .get(`/cash_app/bankfiles/${fileId}/linkages${params}`)
    .catch((error) => {
      handleAPIError(error);
      Toaster(
        getLocaleString(
          "bank_files.error_fetching_linkages",
          "Error fetching linkage information"
        ),
        "danger"
      );
      throw error;
    });
};

export const enqueueLinkageJob = (fileId) =>
  instanceWithRetry
    .post(`/cash_app/bankfiles/${fileId}/enqueue-linkage-job?mutate=1`)
    .catch((error) => {
      handleAPIError(error);
      Toaster(
        getLocaleString(
          "bank_files.error_enqueuing_linkage_job",
          "Error enqueuing linkage job"
        ),
        "danger"
      );
      throw error;
    });

export const getBankFileWithLinkages = (
  fileId,
  includeDetailedLinkages = false
) => {
  const params = includeDetailedLinkages
    ? "?include_detailed_linkages=true"
    : "";
  return instanceWithRetry
    .get(`/cash_app/bankfiles/${fileId}/with-linkages${params}`)
    .catch((error) => {
      handleAPIError(error);
      Toaster(
        getLocaleString(
          "bank_files.error_fetching_file_with_linkages",
          "Error fetching bank file with linkage details"
        ),
        "danger"
      );
      throw error;
    });
};

export const exportBankFileOutput = (fileId, publish = 0) => {
  const params = publish === 1 ? `?mutate=1` : ``;
  return instanceWithRetry
    .get(`/cash_app/bankfiles/${fileId}/export-output/${publish}${params}`)
    .catch((error) => {
      handleAPIError(error);
      Toaster(
        getLocaleString(
          "bank_files.error_exporting_output",
          "Error exporting bank file output"
        ),
        "danger"
      );
      throw error;
    });
};

export const getGroupedLineIds = (fileId) =>
  instanceWithRetry
    .get(`/cash_app/bankfiles/${fileId}/export-output/grouped_ids`)
    .catch((error) => {
      handleAPIError(error);
      Toaster(
        getLocaleString(
          "bank_files.error_fetching_grouped_ids",
          "Error fetching grouped line IDs"
        ),
        "danger"
      );
      throw error;
    });

export const exportBankFileOutputWithLineIds = (
  fileId,
  publish = 0,
  lineIds = [],
  groupName = ""
) => {
  const params =
    publish === 1 ? `?publish=${publish}&mutate=1` : `?publish=${publish}`;
  return instanceWithRetry
    .post(`/cash_app/bankfiles/${fileId}/export-output${params}`, {
      bank_file_line_ids: lineIds,
      suffix: groupName,
    })
    .catch((error) => {
      handleAPIError(error);
      Toaster(
        getLocaleString(
          "bank_files.error_exporting_output",
          "Error exporting bank file output"
        ),
        "danger"
      );
      throw error;
    });
};

export const deleteBankFileLineLinkages = (fileId, bankFileLineId) =>
  instanceWithRetry
    .delete(
      `/cash_app/bankfiles/${fileId}/lines/${bankFileLineId}/linkages?mutate=1`
    )
    .catch((error) => {
      handleAPIError(error);
      Toaster(
        getLocaleString(
          "bank_files.error_deleting_linkages",
          "Error unlinking bank file line"
        ),
        "danger"
      );
      throw error;
    });

export const createBankFileLineOnAccountPosting = (fileId, lineId, data) =>
  instanceWithRetry
    .post(
      `/cash_app/bankfiles/${fileId}/link/${lineId}/create_on_account_posting?mutate=1`,
      data
    )
    .catch((e) => handleAPIError(e));

export const getRunningNumbers = (from_date, to_date = null) => {
  let url = `/cash_app/bankfiles/running-numbers?from_date=${from_date}`;
  if (to_date) {
    url += `&to_date=${to_date}`;
  }
  return instanceWithRetry.get(url).catch((error) => {
    handleAPIError(error);
    Toaster(
      getLocaleString(
        "bank_files.error_fetching_running_numbers",
        "Error fetching running numbers"
      ),
      "danger"
    );
    throw error;
  });
};

/**
 * Fetch AR invoices for a customer relationship (used by the manual allocation page).
 * @param {Object} params query params, e.g. { client_customer_rel_id, page, per_page, sort_by, sort_order }
 */
export const getCustomerInvoices = (params) =>
  instanceWithRetry
    .get(`arinvoice${generateQueryString(params)}`)
    .catch((e) => {
      handleAPIError(e);
      throw e;
    });

/**
 * Read-only pre-validation of a multi-invoice allocation for a bank file line.
 * Resolves with the validation payload (net amount, bank line amount, invoices) or
 * rejects with the API error so the caller can render `error.response.data.message`.
 */
export const preLinkageMultiInvoiceValidation = (fileId, lineId, data) =>
  instanceWithRetry
    .post(
      `/cash_app/bankfiles/${fileId}/link/${lineId}/pre_linkage_multi_invoice_validation`,
      data
    )
    .catch((e) => {
      handleAPIError(e);
      throw e;
    });

/**
 * Read-only invoice linkage suggestion for a bank file line. It pre-selects the
 * invoices of a customer that should be allocated to the line for the requested
 * strategy (see `invoice_linkage_suggestions` in openapi.yaml).
 *
 * @param {Object} params query params, e.g. { client_customer_rel_id, strategy }
 * Resolves with the suggestion payload (same shape as the pre-linkage multi-invoice
 * validation) or rejects with the API error so the caller can render
 * `error.response.data.message` (a `404` simply means nothing could be suggested).
 */
export const getInvoiceLinkageSuggestions = (fileId, lineId, params) =>
  instanceWithRetry
    .get(
      `/cash_app/bankfiles/${fileId}/link/${lineId}/invoice_linkage_suggestions${generateQueryString(
        params
      )}`
    )
    .catch((e) => {
      handleAPIError(e);
      throw e;
    });

/**
 * Create the SPECIFIC_INVOICE linkages between one bank file line and multiple AR invoices.
 */
export const createMultiInvoiceLinkage = (fileId, lineId, data) =>
  instanceWithRetry
    .post(
      `/cash_app/bankfiles/${fileId}/link/${lineId}/create_multi_invoice_linkage?mutate=1`,
      data
    )
    .catch((e) => {
      handleAPIError(e);
      throw e;
    });

export const uploadBankFile = (e) => {
  const bodyFormData = new FormData();
  const fileList = Object.values(e.target.files);
  fileList.forEach((file) => bodyFormData.append("file", file, file.name));
  return instanceMultipart
    .post(`/cash_app/bankfiles/uploadfile?mutate=1`, bodyFormData)
    .catch((e) => handleAPIError(e, true));
};
